import { useCallback, useEffect, useState } from 'react'
import {
    listCatalogItems, updateCatalogItem, publishCatalogItem,
    type CatalogItem,
} from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import InfoTip from '../components/InfoTip'
import { brandName, moneySign } from '../lookups'
import { LOOKUP_FETCH_LIMIT } from '../../shared/constants'
import { plural, fill, PAGES_AUTHOR_CATALOG_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />

// Only a COURSE can belong to an author (operator, 2026-08-10) — recordings and webinars are
// sold too, but nobody is settled for them.
const canHaveAuthor = (type?: string) => !type || type === 'vp_course'

// The commercial side of the articles. WP owns title, type and the editorial release; here the
// price, the fee and the sales release are decided. An article sells only when BOTH releases
// are set — that is why the list shows them side by side rather than one merged status.

const money = (n?: number) => n == null ? '—' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const FEE_LEVEL: Record<string, string> = T.feeLevel

export default function AuthorCatalog() {
    const { openTab, publishChange } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const canWrite = can('invoices', 'write')
    // Refetch when a detail mask saves (or a release is toggled from here).
    const version = useDataVersion('catalog')

    const [rows, setRows] = useState<CatalogItem[]>([])
    const [q, setQ] = useState('')
    const [release, setRelease] = useState('')
    const [busy, setBusy] = useState<string | null>(null)
    const [error, setError] = useState('')

    const load = useCallback(async () => {
        try {
            const items = await listCatalogItems({ q, release, pageSize: LOOKUP_FETCH_LIMIT })
            setRows(items.rows)
            setError('')
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.loadFailed
            setError(msg); toast(msg, 'err')
        }
    }, [q, release, toast])
    useEffect(() => { void load() }, [load, version])

    // One tab per article — the id carries the record, so two articles can be open side by
    // side. itemId travels along because there is no GET for a single item: the mask finds
    // its record through the list endpoint, which also matches on the article id.
    const openEdit = (r: CatalogItem) => openTab({
        id: `catalog-item-${r._id}`,
        name: fill(T.tabName, { title: r.title || `#${r.itemId}` }),
        component: 'catalog-item-form',
        config: { id: r._id, itemId: r.itemId },
    })

    async function toggleRelease(r: CatalogItem) {
        setBusy(r._id)
        try {
            await updateCatalogItem(r._id, { salesReleased: !r.salesReleased })
            // Refreshes this list AND any open detail mask for the article.
            publishChange('catalog')
            toast(!r.salesReleased ? T.salesReleased : T.salesReleaseWithdrawn)
        } catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.failed, 'err') }
        finally { setBusy(null) }
    }

    async function republish(r: CatalogItem) {
        setBusy(r._id)
        try { await publishCatalogItem(r._id); toast(T.republished) }
        catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.failed, 'err') }
        finally { setBusy(null) }
    }

    return (
        <div className="page">
            <div className="page-title">
                <h1>{T.title}</h1>
                <InfoTip>
                    {T.info.part1}<b>{T.info.bold1}</b>{T.info.part2}<b>{T.info.bold2}</b>{T.info.part3}
                </InfoTip>
            </div>

            {error && <div className="notice err">{error}</div>}

            <div className="row" style={{ marginBottom: 14 }}>
                <div className="field" style={{ maxWidth: 260, marginBottom: 0 }}>
                    <label>{T.search}</label>
                    <input type="text" value={q} placeholder={T.searchPlaceholder} onChange={(e) => setQ(e.target.value)} />
                </div>
                <div className="field" style={{ maxWidth: 220, marginBottom: 0 }}>
                    <label>{T.release}</label>
                    <select value={release} onChange={(e) => setRelease(e.target.value)}>
                        <option value="">{T.filterAll}</option>
                        <option value="sellable">{T.sellable}</option>
                        <option value="pending">{T.notSellable}</option>
                    </select>
                </div>
            </div>

            {rows.length === 0 && (
                <div className="notice">
                    {T.emptyBefore}<code>POST /api/catalog/items</code>{T.emptyAfter}
                </div>
            )}

            {rows.length > 0 && (
                <table>
                    <thead>
                        <tr>
                            <th>{T.colItem}</th>
                            <th>{T.colAuthor}</th>
                            <th className="num">{T.colSuggestion}</th>
                            <th className="num">{T.colSalesPrice}</th>
                            <th>{T.colFee}</th>
                            <th>{T.colReleases}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const suggestionDiffers = r.priceSuggestion != null && r.priceSuggestion !== r.salesPrice
                            return (
                                <tr key={r._id} className="row-activatable"
                                    onDoubleClick={() => openEdit(r)}
                                    // suppress the text selection a double-click would otherwise make
                                    onMouseDown={(e) => { if (e.detail > 1) e.preventDefault() }}>
                                    <td>
                                        <div>{r.title || `#${r.itemId}`}</div>
                                        <span className="hint">
                                            {brandName(r.brand)} · #{r.itemId} · {r.type || '—'} · v{r.version}
                                            {r.freeAccessCount > 0 && fill(plural(r.freeAccessCount, T.freeAccess), { n: r.freeAccessCount })}
                                        </span>
                                    </td>
                                    <td>
                                        {canHaveAuthor(r.type)
                                            ? (r.authorName ?? <span className="hint">{T.noAuthor}</span>)
                                            : <span className="hint" title={T.onlyCoursesTitle}>{T.onlyCourses}</span>}
                                    </td>
                                    <td className="num">
                                        {money(r.priceSuggestion)}
                                        {suggestionDiffers && (
                                            <div className="hint" title={T.suggestionDiffersTitle}>{T.suggestionDiffers}</div>
                                        )}
                                    </td>
                                    <td className="num">{money(r.salesPrice)}</td>
                                    <td>
                                        <div>
                                            {r.feeType
                                                ? `${r.feeValue ?? '—'}${r.feeType === 'fixed' ? ` ${moneySign()}` : ' %'}`
                                                : <span className="hint">{T.inherit}</span>}
                                        </div>
                                        <span className="hint">
                                            {canHaveAuthor(r.type)
                                                ? fill(T.feeApplies, { fee: `${r.effectiveFee.value}${r.effectiveFee.type === 'fixed' ? ` ${moneySign()}` : ' %'}`, level: FEE_LEVEL[r.effectiveFee.level] })
                                                : T.feeNoEffect}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="hint">{T.content}{r.contentReleased ? T.contentReleased : T.contentOpen}</div>
                                        <button type="button" className="secondary" disabled={!canWrite || busy !== null}
                                            onClick={() => toggleRelease(r)}>
                                            {r.salesReleased ? T.lockSales : T.releaseSales}
                                        </button>
                                        {r.sellable && <div className="hint" style={{ color: 'var(--ok)' }}>{T.sellable}</div>}
                                    </td>
                                    <td>
                                        <div className="row-actions">
                                            <button type="button" className="icon-action"
                                                title={canWrite ? T.edit : T.view} aria-label={T.edit}
                                                onClick={() => openEdit(r)}><EditIcon /></button>
                                            <button type="button" className="secondary" disabled={!canWrite || busy !== null}
                                                title={T.republishTitle}
                                                onClick={() => republish(r)}>↻</button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}
        </div>
    )
}
